import React from 'react'
import { View } from 'react-native'

import { twMerge } from '../../tailwind/merge'
import { type PunctualityKpiWindow, PunctualityKpiWindowRow } from '../punctuality-kpi-direction'
import { PunctualityMobileTodayRow } from '../punctuality-mobile-today-row'

export const PUNCTUALITY_MOBILE_TILE_VARIANTS = ['C1', 'C2', 'C3'] as const

export type PunctualityMobileTileVariant = (typeof PUNCTUALITY_MOBILE_TILE_VARIANTS)[number]

export type PunctualityMobileTileContentProps = {
    variant?: PunctualityMobileTileVariant
    today: PunctualityKpiWindow
    last60: PunctualityKpiWindow
    next3h: PunctualityKpiWindow
    noPredictionReason?: string
    emptyValueText?: string
    className?: string
    testID?: string
}

const NO_VALUE = '–'

const rootClass: Record<PunctualityMobileTileVariant, string> = {
    C1: 'gap-s',
    C2: 'gap-2xs pt-s',
    C3: 'gap-s pt-s',
}

/**
 * The body of the mobile Punctuality tile for one direction (Figma
 * `Mobile tile · C1 / C2 / C3`, 19138:458015, 19155:461485, 19155:461568).
 * C1 and C3 lead with the large Today value and its caption; C2 draws Today
 * as a third window row. The shell, title and delay threshold line belong to
 * the host tile.
 */
export const PunctualityMobileTileContent = ({
    variant = 'C1',
    today,
    last60,
    next3h,
    noPredictionReason,
    emptyValueText = NO_VALUE,
    className,
    testID = 'punctuality-mobile-tile',
}: PunctualityMobileTileContentProps) => {
    const windows = (
        <>
            <PunctualityKpiWindowRow
                kpi={last60}
                emptyValueText={emptyValueText}
                testID={`${testID}-last60`}
            />
            <PunctualityKpiWindowRow
                kpi={next3h}
                emptyValueText={emptyValueText}
                reason={noPredictionReason}
                testID={`${testID}-next3h`}
            />
        </>
    )

    return (
        <View className={twMerge('w-full min-w-0', rootClass[variant], className)} testID={testID}>
            {variant === 'C2' ? (
                <>
                    <PunctualityKpiWindowRow
                        kpi={today}
                        emptyValueText={emptyValueText}
                        testID={`${testID}-today`}
                    />
                    {windows}
                </>
            ) : (
                <>
                    <PunctualityMobileTodayRow
                        today={today}
                        emptyValueText={emptyValueText}
                        testID={`${testID}-today`}
                    />
                    <View className='w-full gap-2xs'>{windows}</View>
                </>
            )}
        </View>
    )
}

PunctualityMobileTileContent.displayName = 'PunctualityMobileTileContent'
